/**
 * AuthorizationList Component
 * Displays list of authorizations using GenericListView
 * Supports both legacy and PA module records
 */

import React from 'react';
import { Edit, Trash2, Eye, Copy } from 'lucide-react';
import { Authorization, AuthorizationsService, PAStatus, PA_COPYABLE_STATUSES } from '../../../services/patientmr/authorizations.service';
import { SvcBadge } from './components/SvcBadge';
import { PayerChip } from './components/PayerChip';
import { ProgressBar } from './components/ProgressBar';
import { LoadingSpinner, StatusPill, getStatusConfig } from '../../common';

export interface AuthorizationListProps {
  authorizations: Authorization[];
  onView: (id: string | number) => void;
  onEdit: (id: string | number) => void;
  onDelete: (id: string | number) => void;
  onCopy?: (id: string | number) => void;
  loading?: boolean;
}

export function AuthorizationList({
  authorizations,
  onView,
  onEdit,
  onDelete,
  loading = false
}: AuthorizationListProps): React.ReactElement {
  return (
    <div>
      {loading && (
        <LoadingSpinner />
      )}
      {!loading && authorizations.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-8 text-center">
          <div className="mx-auto text-slate-300 mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
            </svg>
          </div>
          <h3 className="text-lg font-medium text-slate-900 mb-2">No authorizations available</h3>
          <p className="text-slate-500 text-sm">Start by creating a new authorization using the button above.</p>
        </div>
      )}
    </div>
  );
}

// PA-aware column configuration
export const getAuthorizationColumns = () => [
  {
    key: 'paId',
    label: 'PA ID',
    width: 'w-32',
    render: (auth: Authorization) => {
      const isPa = AuthorizationsService.isPARecord(auth);
      if (isPa) {
        return <span className="text-sm font-medium text-slate-900">{auth.paNumber || auth.authorizationId || auth.id}</span>;
      }
      return (
        <div>
          <div className="font-medium text-base">{auth.discipline}</div>
          <div className="text-xs text-slate-500 mt-0.5">Auth #{auth.authNumber}</div>
        </div>
      );
    },
  },
  {
    key: 'service',
    label: 'Service',
    width: 'w-20',
    render: (auth: Authorization) => {
      if (auth.serviceCategory) {
        return <SvcBadge serviceCategory={auth.serviceCategory} />;
      }
      return <span className="text-sm text-slate-600">{auth.discipline}</span>;
    },
  },
  {
    key: 'payer',
    label: 'Payer',
    width: 'w-36',
    render: (auth: Authorization) => {
      if (auth.payerType && auth.payerName) {
        return <PayerChip payerType={auth.payerType} payerName={auth.payerName} />;
      }
      return <span className="text-sm text-slate-600">{auth.insuranceProvider || auth.payorName || '\u2014'}</span>;
    },
  },
  {
    key: 'type',
    label: 'Type',
    width: 'w-24',
    render: (auth: Authorization) => {
      if (auth.requestType) {
        const labels: Record<string, string> = { initial: 'Initial', recertification: 'Recert', revision: 'Revision' };
        return <span className="text-sm text-slate-600">{labels[auth.requestType] || auth.requestType}</span>;
      }
      return <span className="text-sm text-slate-600">{auth.authorizationType || '\u2014'}</span>;
    },
  },
  {
    key: 'status',
    label: 'Status',
    width: 'w-24',
    align: 'center' as const,
    render: (auth: Authorization) => {
      const isPa = AuthorizationsService.isPARecord(auth);
      if (isPa) {
        const styles = AuthorizationsService.getPAStatusBadgeStyles(auth.status as string);
        return (
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${styles}`}>
            {auth.status}
          </span>
        );
      }
      const currentStatus = AuthorizationsService.calculateStatus(auth.startDate, auth.expirationDate);
      return (
        <StatusPill status={currentStatus} />
      );
    },
  },
  {
    key: 'period',
    label: 'Period',
    width: 'w-36',
    render: (auth: Authorization) => {
      const start = auth.requestedStartDate || auth.startDate;
      const end = auth.requestedEndDate || auth.expirationDate;
      const daysLeft = auth.approvedEndDate ? AuthorizationsService.getDaysRemaining(auth.approvedEndDate) : null;

      return (
        <div className="text-sm text-slate-600">
          {AuthorizationsService.formatDate(start)} &ndash; {AuthorizationsService.formatDate(end)}
          {daysLeft != null && daysLeft <= 14 && auth.status === PAStatus.APPROVED && (
            <div className="text-xs text-amber-600 mt-0.5">{daysLeft}d left</div>
          )}
        </div>
      );
    },
  },
  {
    key: 'usage',
    label: 'Usage',
    width: 'w-36',
    render: (auth: Authorization) => {
      if (AuthorizationsService.isPARecord(auth) && auth.approvedQuantity) {
        return (
          <ProgressBar
            used={auth.usedQuantity || 0}
            total={auth.approvedQuantity}
            unitType={auth.unitType || 'units'}
            minutesPerUnit={auth.minutesPerUnit || null}
            compact
          />
        );
      }
      if (auth.authorizedVisits || auth.visits) {
        return (
          <div className="text-sm">
            <span className="font-medium text-slate-900">{auth.unitsUsed || 0}</span>
            <span className="text-slate-500">/{auth.authorizedVisits || auth.visits}</span>
          </div>
        );
      }
      return <span className="text-xs text-slate-400">&mdash;</span>;
    },
  },
  {
    key: 'authNum',
    label: 'Auth #',
    width: 'w-28',
    render: (auth: Authorization) => (
      <span className="text-sm text-slate-600 font-mono">{auth.authNumber || '\u2014'}</span>
    ),
  },
];

/** PA records: edit only while DRAFT or PENDED (server returns 409 RECORD_LOCKED otherwise). Legacy records stay editable. */
export const isEditableStatus = (auth: Authorization): boolean =>
  !AuthorizationsService.isPARecord(auth) || auth.status === PAStatus.DRAFT || auth.status === PAStatus.PENDED;

/** Delete is allowed only while DRAFT (server returns 409 RECORD_LOCKED otherwise) */
export const isDeletableStatus = (auth: Authorization): boolean => auth.status === PAStatus.DRAFT;

/** Copy to a new DRAFT is allowed from APPROVED, EXPIRED, EXHAUSTED and DENIED (PA-06, Phase 8). */
export const isCopyableStatus = (auth: Authorization): boolean =>
  AuthorizationsService.isPARecord(auth) && PA_COPYABLE_STATUSES.has(String(auth.status));

// PA-aware action configuration
export const getAuthorizationActions = (
  onView: (id: string | number) => void,
  onEdit: (id: string | number) => void,
  onDelete: (id: string | number) => void,
  onCopy?: (id: string | number) => void,
  canUpdate: boolean = true,
  canDelete: boolean = false,
  isReadOnly: boolean = false
) => {
  // Return a function-based actions array that conditionally renders per-row
  return [
    {
      icon: <Eye size={14} />,
      label: canUpdate ? 'View' : 'View',
      onClick: (auth: Authorization) => onView(auth.id),
      className: canUpdate 
        ? 'p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors'
        : 'p-1.5 text-gray-600 hover:text-gray-700 hover:bg-gray-50 rounded-md transition-colors',
    },
    {
      icon: <Edit size={14} />,
      label: 'Edit',
      onClick: (auth: Authorization) => onEdit(auth.id),
      className: 'p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md transition-colors',
      isVisible: (auth: Authorization) => {
        // Show only if user can update
        if (!canUpdate) return false;
        // Phase 4: PA records are editable only in DRAFT or PENDED
        return isEditableStatus(auth);
      },
    },
    {
      icon: <Trash2 size={14} />,
      label: 'Delete',
      onClick: (auth: Authorization) => onDelete(auth.id),
      className: 'p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors',
      isVisible: (auth: Authorization) => {
        // Hide if user doesn't have delete permission
        if (!canDelete) return false;
        // Hide in read-only mode
        if (isReadOnly) return false;
        // Phase 4: only DRAFT authorizations can be (soft) deleted
        return isDeletableStatus(auth);
      },
    },
    ...(onCopy ? [{
      icon: <Copy size={14} />,
      label: 'Copy to New PA',
      onClick: (auth: Authorization) => onCopy(auth.id),
      className: 'p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors',
      isVisible: (auth: Authorization) => {
        // Hide in read-only mode or without update permission
        if (isReadOnly || !canUpdate) return false;
        return isCopyableStatus(auth);
      },
    }] : []),
  ];
};

// PA-aware mobile card
export const getAuthorizationMobileCard = (
  onView: (id: string | number) => void,
  onEdit: (id: string | number) => void,
  onDelete: (id: string | number) => void,
  onCopy?: (id: string | number) => void,
  canUpdate: boolean = true,
  canDelete: boolean = false,
  isReadOnly: boolean = false
) => ({
  primaryText: (auth: Authorization) => {
    if (AuthorizationsService.isPARecord(auth)) {
      return `${auth.paNumber || auth.authorizationId || auth.id} · ${(auth.serviceCategory || '').toUpperCase()}`;
    }
    return `${auth.discipline} (Auth# ${auth.authNumber})`;
  },
  secondaryText: (auth: Authorization) => {
    const start = auth.requestedStartDate || auth.startDate;
    const end = auth.requestedEndDate || auth.expirationDate;
    return `${AuthorizationsService.formatDate(start)} - ${AuthorizationsService.formatDate(end)}`;
  },
  tertiaryText: (auth: Authorization) => {
    if (auth.payerName) return auth.payerName;
    return `Approved: ${auth.authorizedVisits || auth.visits} units`;
  },
  badges: (auth: Authorization) => {
    if (AuthorizationsService.isPARecord(auth)) {
      return [{
        label: auth.status as string,
        className: AuthorizationsService.getPAStatusBadgeStyles(auth.status as string),
      }];
    }
    const status = AuthorizationsService.calculateStatus(auth.startDate, auth.expirationDate);
    const sc = getStatusConfig(status);
    return [{
      label: sc.label,
      className: sc.className,
    }];
  },
  actions: (auth: Authorization) => {
    const actions = [
      { icon: <Eye size={18} />, label: 'View', onClick: () => onView(auth.id) },
    ];
    if (canUpdate && isEditableStatus(auth)) {
      actions.push({ icon: <Edit size={18} />, label: 'Edit', onClick: () => onEdit(auth.id) });
    }
    // Only show delete if user has delete permission and not in read-only mode
    if (canDelete && !isReadOnly && isDeletableStatus(auth)) {
      actions.push({ icon: <Trash2 size={18} />, label: 'Delete', onClick: () => onDelete(auth.id) });
    }
    if (onCopy && canUpdate && !isReadOnly && isCopyableStatus(auth)) {
      actions.push({ icon: <Copy size={18} />, label: 'Copy', onClick: () => onCopy(auth.id) });
    }
    return actions;
  },
});

export default AuthorizationList;
